import { Component, EventEmitter, Input, Output, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { EstablishmentOfferCatalog } from '../../core/services/prevention.service';
import { PreventionOffersService } from '../../core/services/prevention-offers.service';

/**
 * Catalogue du groupe vu depuis un site -- restaure l'étape intermédiaire retirée par erreur lors de
 * l'intégration de la Bibliothèque Waitora dans Site (`Ajouter une offre` ouvrait Waitora directement,
 * contredisant l'architecture : un site ne pioche que dans le catalogue de SON groupe, jamais Waitora).
 * `offers` provient directement de `SiteComponent.preventionCatalog()` (déjà chargé) -- aucun appel
 * HTTP propre à ce composant.
 */
@Component({
  selector: 'app-prevention-site-add-offer',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './prevention-site-add-offer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./prevention-site-add-offer.component.scss'],
})
export class PreventionSiteAddOfferComponent {
  @Input() offers: EstablishmentOfferCatalog[] = [];
  @Output() back = new EventEmitter<void>();
  @Output() activateOffer = new EventEmitter<string>();
  @Output() viewDetails = new EventEmitter<string>();
  @Output() createOffer = new EventEmitter<void>();

  private readonly offersSvc = inject(PreventionOffersService);

  readonly search = signal('');

  /** URL de la vignette de l'offre, ou `null` si elle n'a pas de photo. */
  photoUrl(offer: EstablishmentOfferCatalog): string | null {
    return offer.hasPhoto ? this.offersSvc.getPhotoUrl(offer.offerId, offer.photoUpdatedAt) : null;
  }

  onSearchChanged(value: string): void {
    this.search.set(value);
  }

  private matches(offer: EstablishmentOfferCatalog, query: string): boolean {
    if (offer.name.toLowerCase().includes(query)) return true;
    if (offer.description?.toLowerCase().includes(query)) return true;
    return offer.bilans.some(bilan => bilan.name.toLowerCase().includes(query));
  }

  filteredOffers(): EstablishmentOfferCatalog[] {
    const query = this.search().trim().toLowerCase();
    if (!query) return this.offers;
    return this.offers.filter(o => this.matches(o, query));
  }

  isGloballyEmpty(): boolean {
    return this.offers.length === 0;
  }

  hasSearchNoResults(): boolean {
    return !this.isGloballyEmpty() && this.search().trim().length > 0 && this.filteredOffers().length === 0;
  }

  bilanNames(offer: EstablishmentOfferCatalog): string {
    return offer.bilans.map(b => b.name).join(' + ');
  }
}
